/**
 * OpsPage — #56 PR 1b (DEC-362). The Paelode admin ops console: Operations → Health and
 * Errors. Reads GET /v1/platform/ops/{health,events} (PR 1a), polling every 15 s while
 * the tab is visible.
 *
 *   Health — the worst status and why, error counts (15 min / 1 h / 24 h), the last hour
 *            by area, the top problems, queue backlogs and scheduled-job freshness.
 *   Errors — newest first, filtered by area / minimum severity / time range; one search
 *            box takes a refCode (ERR-XXXX), an error id (err_…) or a request id (req_…).
 *            Clicking a tenant filters to it, so no id is ever typed.
 *
 * #56 PR 3a — Health also shows the CloudWatch numbers: the API's 5xx share and p95
 * latency, its servers' health checks, PDF renderer errors and the SES bounce rate. When
 * they cannot be read the tiles say so; they never show a reassuring zero.
 *
 * OPEN-195 PR 1 (DEC-391) — every row that shows a problem opens the problem page
 * (/ops/problem, ProblemPage.tsx): an area row and a top problem for the last hour, an Errors
 * row for its event in the chosen range. The three count cards open Errors for their window.
 * OPEN-191 (ops) — the tab and the Errors and Incidents filters live in the URL
 * (lib/url-state.ts), so Back from a problem or an incident lands on the same view.
 */
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Pill, fieldStyle, labelStyle, searchInputStyle, subText } from '../../ui/bits';
import { listFrom, useUrlParam } from '../../lib/url-state';
import {
  opsProblemQuery,
  useOpsEvents,
  useOpsHealth,
  type OpsCategory,
  type OpsCloud,
  type OpsEvent,
  type OpsEventsFilter,
  type OpsHealth,
  type OpsProblemFilter,
  type OpsSeverity,
  type SeverityCounts,
} from './hooks';
import {
  OPS_CATEGORY_OPTIONS,
  STATUS_LABEL,
  ageLabel,
  categoryLabel,
  eventLabel,
  parseSearch,
  severityTone,
  statusTone,
  timeAgo,
} from './labels';
import { IncidentsTab, RecipientsTab, RulesTab } from './AlertsTabs';
import { LogsTab, type LogTarget } from './LogsTab';
import { TAB_TRACK_STYLE, tabPillStyle } from 'src/components/ui/tab-styles';

type Tab = 'health' | 'errors' | 'logs' | 'incidents' | 'rules' | 'recipients';

const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'health', label: 'Health' },
  { id: 'errors', label: 'Errors' },
  // #56 PR 3b — the templated CloudWatch Logs Insights lookup.
  { id: 'logs', label: 'Raw logs' },
  // #56 PR 2c (DEC-363) — email alerts.
  { id: 'incidents', label: 'Incidents' },
  { id: 'rules', label: 'Rules' },
  { id: 'recipients', label: 'Recipients' },
];

const isTab = (v: string): v is Tab => TABS.some((t) => t.id === v);

const cardStyle: React.CSSProperties = {
  flex: 1,
  minWidth: '200px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '16px',
};

const sectionTitle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  margin: '24px 0 8px',
};

const linkStyle: React.CSSProperties = { color: 'var(--color-link)', textDecoration: 'none' };

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
/** The API's longest problem window. */
const PROBLEM_MAX_MS = 31 * DAY_MS;

/** The problem page for a filter. Exported for the tests. */
export function problemHref(f: OpsProblemFilter): string {
  return `/ops/problem?${opsProblemQuery(f)}`;
}

/** The end of the current minute: a window ending here includes what just happened. */
function minuteEnd(now = Date.now()): number {
  return Math.floor(now / MINUTE_MS) * MINUTE_MS + MINUTE_MS;
}

/** The last hour, to the minute, so the link stays the same for a minute. */
function lastHour(now = Date.now()): { from: string; to: string } {
  const to = minuteEnd(now);
  return { from: new Date(to - HOUR_MS).toISOString(), to: new Date(to).toISOString() };
}

function Counts({
  title,
  c,
  to,
}: {
  title: string;
  c: SeverityCounts;
  /** OPEN-195: the Errors tab for this window. */
  to: string;
}): React.JSX.Element {
  return (
    <Link
      to={to}
      style={{ ...cardStyle, color: 'inherit', textDecoration: 'none' }}
      aria-label={title}
    >
      <div style={{ ...subText, textTransform: 'none', marginBottom: '6px' }}>{title}</div>
      <div style={{ display: 'flex', gap: '16px', fontSize: '14px' }}>
        <span>
          <strong style={{ fontSize: '20px' }}>{c.CRITICAL}</strong> critical
        </span>
        <span>
          <strong style={{ fontSize: '20px' }}>{c.ERROR}</strong> errors
        </span>
        <span>
          <strong style={{ fontSize: '20px' }}>{c.WARNING}</strong> warnings
        </span>
      </div>
    </Link>
  );
}

function Tile({
  title,
  value,
  detail,
  warn = false,
}: {
  title: string;
  value: string;
  detail?: string | undefined;
  warn?: boolean;
}): React.JSX.Element {
  return (
    <div style={cardStyle} aria-label={title}>
      <div style={{ ...subText, textTransform: 'none', marginBottom: '6px' }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <strong style={{ fontSize: '20px' }}>{value}</strong>
        {warn && <Pill label="Check" tone="warning" />}
      </div>
      {detail !== undefined && <div style={subText}>{detail}</div>}
    </div>
  );
}

const pct = (n: number): string => `${String(n)}%`;
const ms = (n: number | null): string => (n === null ? 'No traffic' : `${String(n)} ms`);

/** Exported for the tests. */
export function CloudTiles({ c }: { c: OpsCloud | undefined }): React.JSX.Element {
  if (c === undefined || c.status !== 'OK' || c.api === null) {
    const why =
      c?.status === 'UNAVAILABLE'
        ? 'CloudWatch could not be read just now. These numbers are unknown, not zero.'
        : 'CloudWatch numbers are not available in this environment.';
    return (
      <p style={{ ...subText, textTransform: 'none' }} role="status">
        {why}
      </p>
    );
  }
  const a = c.api;
  const hosts =
    a.healthyHosts === null
      ? 'Unknown'
      : `${String(a.healthyHosts)} healthy${
          a.unhealthyHosts !== null && a.unhealthyHosts > 0
            ? `, ${String(a.unhealthyHosts)} failing`
            : ''
        }`;
  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      <Tile
        title="API server errors, last 5 minutes"
        value={pct(a.last5m.errorPercent)}
        detail={`${String(a.last5m.errors)} of ${String(a.last5m.requests)} requests · last hour ${pct(
          a.last1h.errorPercent,
        )}`}
        warn={a.last5m.requests >= 50 && a.last5m.errorPercent >= 1}
      />
      <Tile
        title="Response time (p95), last 5 minutes"
        value={ms(a.p95Ms5m)}
        detail={
          a.worstP95Ms1h === null ? undefined : `Worst in the last hour: ${ms(a.worstP95Ms1h)}`
        }
      />
      <Tile
        title="API servers"
        value={hosts}
        warn={a.healthyHosts === 0 || (a.unhealthyHosts ?? 0) > 0}
      />
      <Tile
        title="PDF renderer errors, last hour"
        value={c.rendererErrorsLast1h === null ? 'Unknown' : String(c.rendererErrorsLast1h)}
        warn={(c.rendererErrorsLast1h ?? 0) > 0}
      />
      <Tile
        title="Email bounce rate (SES)"
        value={c.sesBounceRatePercent === null ? 'No data' : pct(c.sesBounceRatePercent)}
        detail="SES pauses sending at about 10%"
        warn={(c.sesBounceRatePercent ?? 0) >= 5}
      />
    </div>
  );
}

function HealthPanel({ h }: { h: OpsHealth }): React.JSX.Element {
  const location = useLocation();
  const from = listFrom(location);
  const hour = lastHour();
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <span style={{ fontSize: '18px', fontWeight: 700 }}>Status</span>
        <Pill label={STATUS_LABEL[h.status]} tone={statusTone(h.status)} />
        <span style={subText}>Updated {timeAgo(h.generatedAt)}</span>
      </div>
      {h.reasons.length > 0 ? (
        <ul aria-label="Why" style={{ margin: '0 0 12px 18px', fontSize: '14px' }}>
          {h.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      ) : (
        <p style={{ fontSize: '14px', margin: '0 0 12px' }}>Nothing needs attention.</p>
      )}

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <Counts
          title="Last 15 minutes"
          c={h.events.last15m}
          to="/ops?tab=errors&severity=WARNING&range=15m"
        />
        <Counts
          title="Last hour"
          c={h.events.last1h}
          to="/ops?tab=errors&severity=WARNING&range=1h"
        />
        <Counts title="Last 24 hours" c={h.events.last24h} to="/ops?tab=errors&severity=WARNING" />
      </div>

      <h3 style={sectionTitle}>API and AWS</h3>
      <CloudTiles c={h.cloud} />

      <h3 style={sectionTitle}>Last hour by area</h3>
      {h.events.last1hByCategory.length === 0 ? (
        <p style={subText}>No problems in the last hour.</p>
      ) : (
        <Table aria-label="Last hour by area">
          <TableHeader>
            <TableRow>
              <TableHead>Area</TableHead>
              <TableHead>Critical</TableHead>
              <TableHead>Errors</TableHead>
              <TableHead>Warnings</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {h.events.last1hByCategory.map((c) => (
              <TableRow key={c.category}>
                <TableCell>
                  <Link
                    to={problemHref({ category: c.category, severity: 'WARNING', ...hour })}
                    state={from}
                    style={linkStyle}
                  >
                    {categoryLabel(c.category)}
                  </Link>
                </TableCell>
                <TableCell>{c.CRITICAL}</TableCell>
                <TableCell>{c.ERROR}</TableCell>
                <TableCell>{c.WARNING}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {h.events.topProblemsLast1h.length > 0 && (
        <>
          <h3 style={sectionTitle}>Top problems, last hour</h3>
          <Table aria-label="Top problems">
            <TableHeader>
              <TableRow>
                <TableHead>Problem</TableHead>
                <TableHead>Area</TableHead>
                <TableHead>Times</TableHead>
                <TableHead>Last seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {h.events.topProblemsLast1h.map((p) => (
                <TableRow key={`${p.event}|${p.code ?? ''}|${p.severity}|${p.category}`}>
                  <TableCell>
                    <Pill label={p.severity} tone={severityTone(p.severity)} />{' '}
                    <Link
                      to={problemHref({
                        event: p.event,
                        ...(p.code !== null ? { code: p.code } : {}),
                        category: p.category,
                        severity: p.severity,
                        ...hour,
                      })}
                      state={from}
                      style={linkStyle}
                    >
                      {eventLabel(p.event)}
                    </Link>
                    {p.code !== null && <span style={subText}> · {p.code}</span>}
                  </TableCell>
                  <TableCell>{categoryLabel(p.category)}</TableCell>
                  <TableCell>{p.occurrences}</TableCell>
                  <TableCell>{timeAgo(p.lastOccurredAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <h3 style={sectionTitle}>Queues</h3>
      <Table aria-label="Queues">
        <TableHeader>
          <TableRow>
            <TableHead>Queue</TableHead>
            <TableHead>Waiting</TableHead>
            <TableHead>Oldest</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {h.queues.map((q) => (
            <TableRow key={q.queue}>
              <TableCell>{q.label}</TableCell>
              <TableCell>{q.depth}</TableCell>
              <TableCell>{ageLabel(q.oldestAgeSeconds)}</TableCell>
              <TableCell>{q.stale && <Pill label="Backed up" tone="warning" />}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <h3 style={sectionTitle}>Scheduled jobs</h3>
      <Table aria-label="Scheduled jobs">
        <TableHeader>
          <TableRow>
            <TableHead>Job</TableHead>
            <TableHead>Last run</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {h.schedulers.map((s) => (
            <TableRow key={s.job}>
              <TableCell>{s.label}</TableCell>
              <TableCell>{timeAgo(s.lastRunAt)}</TableCell>
              <TableCell>
                {s.failed && <Pill label="Last run failed" tone="danger" />}{' '}
                {s.stale && <Pill label="Overdue" tone="warning" />}
                {s.lastRunAt === null && <Pill label="Never run" tone="muted" />}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const RANGES: ReadonlyArray<{ id: string; label: string; ms: number | null }> = [
  // OPEN-195: the "Last 15 minutes" card opens this.
  { id: '15m', label: 'Last 15 minutes', ms: 15 * 60 * 1000 },
  { id: '1h', label: 'Last hour', ms: 60 * 60 * 1000 },
  { id: '24h', label: 'Last 24 hours', ms: null },
  { id: '7d', label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
  { id: '30d', label: 'Last 30 days', ms: 30 * 24 * 60 * 60 * 1000 },
];

/** `value` is the URL's `severity`; ALL (not empty) so "Everything" survives in the URL. */
const SEVERITIES: ReadonlyArray<{ value: string; severity: OpsSeverity | ''; label: string }> = [
  { value: 'ERROR', severity: 'ERROR', label: 'Errors and critical' },
  { value: 'WARNING', severity: 'WARNING', label: 'Warnings and up' },
  { value: 'ALL', severity: '', label: 'Everything' },
];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * OPEN-195: the problem page for an Errors row: its event, code and area over the chosen range,
 * stretched back to the row's own start when it began earlier, at most 31 days. Exported for
 * the tests.
 */
export function errorsRowProblem(e: OpsEvent, rangeId: string, now = Date.now()): OpsProblemFilter {
  const to = minuteEnd(now);
  const span = RANGES.find((r) => r.id === rangeId)?.ms ?? DAY_MS;
  const first = new Date(e.occurredAt).getTime();
  const from = Math.max(Math.min(to - span, first - MINUTE_MS), to - PROBLEM_MAX_MS);
  return {
    event: e.event,
    ...(e.code !== null ? { code: e.code } : {}),
    category: e.category,
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
  };
}

/** The window start, rounded to the minute so the query key changes at most once a minute. */
function rangeFrom(rangeId: string): string | undefined {
  const r = RANGES.find((x) => x.id === rangeId);
  if (r === undefined || r.ms === null) return undefined;
  const minute = Math.floor(Date.now() / 60_000) * 60_000;
  return new Date(minute - r.ms).toISOString();
}

const QUARTER_HOUR_MS = 15 * 60_000;

/** Exported for the tests: the raw-log search an Errors row opens (±15 min around it). */
export function rawLogTarget(e: OpsEvent, now = Date.now()): LogTarget | null {
  const value = e.errorId ?? e.requestId;
  if (value === null) return null;
  const first = new Date(e.occurredAt).getTime();
  const last = new Date(e.lastOccurredAt).getTime();
  // A coalesced row can span hours; the lookup takes at most 24, so start at its first.
  const end = Math.min(last - first > 23 * 60 * 60_000 ? first : last, now) + QUARTER_HOUR_MS;
  return {
    template: e.errorId !== null ? 'errorId' : 'requestId',
    value,
    from: new Date(first - QUARTER_HOUR_MS).toISOString(),
    to: new Date(Math.min(end, now)).toISOString(),
  };
}

function EventRow({
  e,
  rangeId,
  onTenant,
  onRawLog,
}: {
  e: OpsEvent;
  rangeId: string;
  onTenant: (id: string) => void;
  onRawLog: (t: LogTarget) => void;
}): React.JSX.Element {
  const raw = rawLogTarget(e);
  const location = useLocation();
  return (
    <TableRow>
      <TableCell>
        <div>{new Date(e.occurredAt).toLocaleString()}</div>
        {e.occurrences > 1 && (
          <div style={subText}>
            ×{e.occurrences}, last {timeAgo(e.lastOccurredAt)}
          </div>
        )}
      </TableCell>
      <TableCell>
        <Pill label={e.severity} tone={severityTone(e.severity)} />
      </TableCell>
      <TableCell>
        <div>
          <Link
            to={problemHref(errorsRowProblem(e, rangeId))}
            state={listFrom(location)}
            style={linkStyle}
          >
            {eventLabel(e.event)}
          </Link>
        </div>
        <div style={subText}>
          {categoryLabel(e.category)}
          {e.code !== null && ` · ${e.code}`}
          {e.statusCode !== null && ` · ${String(e.statusCode)}`}
        </div>
      </TableCell>
      <TableCell>
        {e.tenantId !== null && e.tenantName !== null ? (
          <button
            type="button"
            onClick={(): void => onTenant(e.tenantId ?? '')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--color-link)',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            {e.tenantName}
          </button>
        ) : (
          <span style={subText}>—</span>
        )}
      </TableCell>
      <TableCell>
        {e.route !== null && (
          <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{e.route}</div>
        )}
        {e.message !== null && <div style={subText}>{e.message}</div>}
      </TableCell>
      <TableCell>
        {e.refCode !== null && <div style={{ fontFamily: 'monospace' }}>{e.refCode}</div>}
        {raw !== null && (
          <button
            type="button"
            onClick={(): void => onRawLog(raw)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--color-link)',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            Raw log
          </button>
        )}
      </TableCell>
    </TableRow>
  );
}

function ErrorsPanel({ onRawLog }: { onRawLog: (t: LogTarget) => void }): React.JSX.Element {
  // OPEN-191: every filter is in the URL (no resets: the page cursor below is local).
  const [search, setSearch] = useUrlParam('q', '', []);
  const [areaParam, setCategory] = useUrlParam('area', '', []);
  const [severityParam, setSeverity] = useUrlParam('severity', 'ERROR', []);
  const [rangeParam, setRange] = useUrlParam('range', '24h', []);
  const [tenantParam, setTenant] = useUrlParam('tenant', '', []);
  const [before, setBefore] = React.useState<string | undefined>(undefined);

  // A hand-edited URL falls back to the defaults rather than sending the API junk.
  const category: OpsCategory | '' = (OPS_CATEGORY_OPTIONS as readonly string[]).includes(areaParam)
    ? (areaParam as OpsCategory)
    : '';
  const severityOption = SEVERITIES.find((s) => s.value === severityParam) ?? SEVERITIES[0];
  const severity: OpsSeverity | '' = severityOption?.severity ?? 'ERROR';
  const range = RANGES.some((r) => r.id === rangeParam) ? rangeParam : '24h';
  const tenantId = UUID_RE.test(tenantParam) ? tenantParam : null;

  const target = parseSearch(search);
  const filter: OpsEventsFilter = {
    ...(target.kind === 'refCode' ? { refCode: target.value } : {}),
    ...(target.kind === 'errorId' ? { errorId: target.value } : {}),
    ...(target.kind === 'requestId' ? { requestId: target.value } : {}),
    ...(tenantId !== null ? { tenantId } : {}),
    ...(category !== '' ? { category } : {}),
    // A lookup by an exact id ignores the severity filter: show whatever it was.
    ...(severity !== '' && target.kind !== 'errorId' && target.kind !== 'requestId'
      ? { severity }
      : {}),
    // An exact id searches the API's long default window; the range applies otherwise.
    ...(target.kind === 'errorId' || target.kind === 'requestId'
      ? {}
      : (() => {
          const from = rangeFrom(range);
          return from === undefined ? {} : { from };
        })()),
    ...(before !== undefined ? { before } : {}),
  };
  const { data, isLoading, isError } = useOpsEvents(filter);
  // The tenant's name comes from its own rows (the list is filtered to it), never typed.
  const tenantName =
    tenantId === null
      ? null
      : (data?.events.find((e) => e.tenantId === tenantId)?.tenantName ?? 'this tenant');

  // Any filter change starts again from the newest page.
  const reset =
    <T,>(set: (v: T) => void) =>
    (v: T): void => {
      setBefore(undefined);
      set(v);
    };

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ flex: 2, minWidth: '240px' }}>
          <span style={labelStyle}>Find</span>
          <input
            aria-label="Find by refCode, error id or request id"
            placeholder="ERR-XXXX, err_… or req_…"
            value={search}
            onChange={(ev): void => reset(setSearch)(ev.target.value)}
            style={searchInputStyle}
          />
        </label>
        <label style={{ flex: 1, minWidth: '150px' }}>
          <span style={labelStyle}>Area</span>
          <select
            aria-label="Area"
            value={category}
            onChange={(ev): void => reset(setCategory)(ev.target.value)}
            style={fieldStyle}
          >
            <option value="">All areas</option>
            {OPS_CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {categoryLabel(c)}
              </option>
            ))}
          </select>
        </label>
        <label style={{ flex: 1, minWidth: '170px' }}>
          <span style={labelStyle}>Show</span>
          <select
            aria-label="Severity"
            value={severityOption?.value ?? 'ERROR'}
            onChange={(ev): void => reset(setSeverity)(ev.target.value)}
            style={fieldStyle}
          >
            {SEVERITIES.map((s) => (
              <option key={s.label} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ flex: 1, minWidth: '150px' }}>
          <span style={labelStyle}>When</span>
          <select
            aria-label="Time range"
            value={range}
            onChange={(ev): void => reset(setRange)(ev.target.value)}
            style={fieldStyle}
          >
            {RANGES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {target.kind === 'invalid' && (
        <p role="alert" style={{ ...subText, color: 'var(--color-warning)' }}>
          Enter a refCode (ERR-XXXX), an error id (err_…) or a request id (req_…).
        </p>
      )}
      {target.kind === 'refCode' && (
        <p style={subText}>
          A refCode is only 4 characters, so it is searched inside the time range. Every match is
          shown.
        </p>
      )}
      {tenantId !== null && tenantName !== null && (
        <p style={{ fontSize: '13px' }}>
          Tenant: <strong>{tenantName}</strong>{' '}
          <button
            type="button"
            onClick={(): void => reset(setTenant)('')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-link)',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>{' '}
          <button
            type="button"
            onClick={(): void =>
              onRawLog({ template: 'tenant', value: tenantId, label: tenantName })
            }
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-link)',
              cursor: 'pointer',
            }}
          >
            Raw logs (warnings and errors)
          </button>
        </p>
      )}

      {target.kind === 'invalid' ? null : isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || data === undefined ? (
        <p role="alert" style={subText}>
          Could not load events.
        </p>
      ) : data.events.length === 0 ? (
        <p style={{ fontSize: '14px', marginTop: '12px' }}>No events match.</p>
      ) : (
        <>
          <Table aria-label="Events">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>What</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Detail</TableHead>
                <TableHead>Ref</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.events.map((e) => (
                <EventRow
                  key={e.id}
                  e={e}
                  rangeId={range}
                  onTenant={(id): void => reset(setTenant)(id)}
                  onRawLog={onRawLog}
                />
              ))}
            </TableBody>
          </Table>
          <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
            {before !== undefined && (
              <button type="button" onClick={(): void => setBefore(undefined)}>
                Newest
              </button>
            )}
            {data.nextCursor !== null && (
              <button type="button" onClick={(): void => setBefore(data.nextCursor ?? undefined)}>
                Older
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function HealthTab(): React.JSX.Element {
  const { data, isLoading, isError } = useOpsHealth();
  if (isLoading) return <p style={subText}>Loading…</p>;
  if (isError || data === undefined) {
    return (
      <p role="alert" style={subText}>
        Could not load health.
      </p>
    );
  }
  return <HealthPanel h={data} />;
}

/** Router state the problem page's "Raw log" sends (/ops?tab=logs). */
export interface OpsLogState {
  readonly logTarget?: LogTarget;
}

export function OpsPage(): React.JSX.Element {
  // OPEN-191: the tab is in the URL (?tab=, Health left out), so Back returns to it.
  const [tabParam, setTabParam] = useUrlParam('tab', 'health', []);
  const tab: Tab = isTab(tabParam) ? tabParam : 'health';
  const setTab = (t: Tab): void => setTabParam(t);
  const location = useLocation();
  // What the Errors tab (or the problem page) asked the Raw logs tab to search; a new object
  // remounts it.
  const [logTarget, setLogTarget] = React.useState<LogTarget | null>(
    () => (location.state as OpsLogState | null)?.logTarget ?? null,
  );
  const openRawLog = (t: LogTarget): void => {
    setLogTarget(t);
    setTab('logs');
  };
  const tabStyle = tabPillStyle;
  return (
    <div style={{ padding: '24px', maxWidth: '1200px' }}>
      <h1 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>Operations</h1>
      <p style={{ ...subText, textTransform: 'none', marginBottom: '16px' }}>
        Problems across every tenant, refreshed every 15 seconds.
      </p>
      <div role="tablist" style={{ ...TAB_TRACK_STYLE, marginBottom: '16px' }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={(): void => setTab(t.id)}
            style={tabStyle(tab === t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'health' && <HealthTab />}
      {tab === 'errors' && <ErrorsPanel onRawLog={openRawLog} />}
      {tab === 'logs' && (
        <LogsTab
          key={
            logTarget === null
              ? 'blank'
              : `${logTarget.template}:${logTarget.value}:${logTarget.from ?? ''}`
          }
          initial={logTarget}
        />
      )}
      {tab === 'incidents' && <IncidentsTab />}
      {tab === 'rules' && <RulesTab />}
      {tab === 'recipients' && <RecipientsTab />}
    </div>
  );
}
